import { rd } from '@passionware/monads';
import { injectConfig } from '@passionware/platform-react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { Resource } from '__types__';
import { resourceQueryUtils } from 'v5/api/resource/resource.api';
import { ItemSelector } from 'v5/patterns/widgets/ItemSelector';
import { UserAvatar } from 'v5/patterns/widgets/user-avatar/UserAvatar';
import { rowLevelValidation } from 'v5/platform/row-level-validation';
import { resourceDisplayName } from 'v5/patterns/resource/resourceDisplayName';
import { WithServices } from 'v5/platform/ts/services';
import { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';

function ResourceSearchOption({ resource }: { resource: Resource }) {
  const primaryEmail = resource.emails?.[0]?.value_1?.trim();

  return (
    <div className="flex min-w-0 flex-1 items-center gap-2.5">
      <UserAvatar
        className="[&_img]:size-full!"
        user={rd.of({
          avatarUrl: resource.profile_picture,
          name: resourceDisplayName(resource)
        })}
        size="sm"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-base font-medium">
          {resourceDisplayName(resource)}
        </p>
        {primaryEmail ? (
          <p className="truncate text-sm text-fg-muted">{primaryEmail}</p>
        ) : null}
      </div>
    </div>
  );
}

function ResourceSearchEmptyContent() {
  const { t } = useTranslation('availability-request', {
    keyPrefix: 'RequestAvailabilityDialog'
  });
  return (
    <div className="p-3 text-base text-fg-muted">{t('noSearchResults')}</div>
  );
}

const RequestAvailabilityResourceSearchInner = injectConfig(ItemSelector<void>)
  .fromProps<WithServices<[WithResourceService]>>(api => ({
    useOptions: (query, isDropdownOpen) => {
      const { services } = api.useProps();
      return rd.map(
        services.resourceService.useList(
          isDropdownOpen
            ? resourceQueryUtils.setSearch(
                resourceQueryUtils.ofDefault(),
                query
              )
            : undefined
        ),
        response =>
          rowLevelValidation
            .validDataFromRows(response.items)
            .map(resource => ({
              id: resource.id.toString(),
              label: resourceDisplayName(resource),
              content: <ResourceSearchOption resource={resource} />
            }))
      );
    },
    emptyContent: <ResourceSearchEmptyContent />
  }))
  .transformProps(x => x.passAll);

export type RequestAvailabilityResourceSearchProps = WithServices<
  [WithResourceService]
> & {
  onResourceSelect: (resourceId: number) => void;
  children: ReactElement;
};

export function RequestAvailabilityResourceSearch({
  onResourceSelect,
  children,
  services
}: RequestAvailabilityResourceSearchProps) {
  const { t } = useTranslation('availability-request', {
    keyPrefix: 'RequestAvailabilityDialog'
  });

  return (
    <RequestAvailabilityResourceSearchInner
      services={services}
      placeholder={t('searchPlaceholder')}
      popoverClassName="min-w-80"
      closeOnSelect
      onSelect={id => onResourceSelect(Number(id))}
    >
      {children}
    </RequestAvailabilityResourceSearchInner>
  );
}
